/**
 * validx Auth Bridge Utility
 *
 * Generates workspace-flow bridge URLs that auto-authenticate users when
 * navigating from aibot to validx (builder). Falls back to raw validx URL
 * on failure.
 *
 * New flow (2026-04):
 *   Uses POST /api/workspace/request-agent on aibot, which signs an HMAC
 *   bridge URL for the CLIENT FOUNDER's email against the target workspace.
 *   This enters validx as a member of the client workspace, so workspace-
 *   scoped authorization resolves correctly — even for consultants viewing
 *   a client.
 *
 * Legacy `/agents/bridge-token` flow removed — it signed by consultant_id,
 * which resolved to the wrong User for client-scoped pages and broke
 * workspace-member authorization on validx.
 */

import { getApiBaseUrl } from '../services/api';

/**
 * Generate a validx bridge URL that auto-authenticates the current user
 * into the target client's workspace.
 *
 * @param validxAppUrl - The base validx URL (e.g. https://builder.agentsanywhere.ai)
 * @param targetPath - The validx path to navigate to (e.g. /customer/project/new/builder)
 * @param extraParams - Additional query params to append to the redirect.
 *                      MUST include `clientId` (= workspaceId on validx) so the
 *                      bridge can sign for the correct workspace.
 * @returns The bridge URL (or raw validx URL as fallback)
 */
export async function getValidxBridgeUrl(
  validxAppUrl: string,
  targetPath: string,
  extraParams?: Record<string, string>,
): Promise<string> {
  // Build the full redirect path with extra params.
  let redirect = targetPath;
  if (extraParams && Object.keys(extraParams).length > 0) {
    const filtered = Object.fromEntries(
      // returnUrl retired 2026-07-22 — the builder workspace has no back-to-aibot
      // button anymore, and the param only bloated every bridged URL.
      Object.entries(extraParams).filter(([k, v]) => k !== 'returnUrl' && v != null && v !== ''),
    );
    if (Object.keys(filtered).length > 0) {
      const sep = redirect.includes('?') ? '&' : '?';
      redirect = `${redirect}${sep}${new URLSearchParams(filtered).toString()}`;
    }
  }

  // workspaceId is the aibot client UUID — it must match the validx Workspace.id.
  // Accept both `clientId` (historical alias) and `workspaceId` (future-facing).
  const workspaceId =
    extraParams?.workspaceId ||
    extraParams?.clientId ||
    '';

  // Extract a builder projectId from the target path if present.
  // Path shape: /customer/project/<id>/...
  // Used as a fallback when the caller doesn't have workspaceId in scope
  // (consultant project rows); aibot resolves workspace via project_agent.
  const projectMatch = targetPath.match(/\/customer\/project\/([^/?#]+)/);
  const projectId =
    projectMatch && projectMatch[1] && projectMatch[1] !== 'new'
      ? projectMatch[1]
      : undefined;

  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('No auth token');

    const apiBase = getApiBaseUrl();
    const resp = await fetch(`${apiBase}/workspace/request-agent`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        workspace_id: workspaceId || undefined,
        project_id: projectId,
        redirect,
      }),
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`request-agent failed: ${resp.status} ${text}`);
    }
    const data = await resp.json();
    if (data.url) {
      console.log('[validxBridge] Got workspace-flow bridge URL');
      return data.url;
    }
    throw new Error('No url in response');
  } catch (err) {
    console.warn('[validxBridge] Bridge failed, using raw URL (user will see login page):', err);
    return `${validxAppUrl}${redirect}`;
  }
}
